
import { PureComponent } from 'react';

import { MESSAGE_TYPE_LOCAL } from '../constants';

export type Props = {

    /**
     * The messages array to render.
     */
    messages: Array<Object>
}

/**
 * Abstract component to display a list of chat messages, grouped by sender.
 *
 * @augments PureComponent
 */
export default class AbstractMessageContainer<P: Props> extends PureComponent<P> {
    static defaultProps = {
        messages: []
    };

    _groupsCacheKey: ?Array<Object>;

    _groupsCacheValue: Array<Array<Object>>;

    /**
     * Iterates over all the messages and creates nested arrays which hold
     * consecutive messages sent by the same person. Messages a signed in user
     * sent from different tabs or devices belong to the same person and
     * therefore to the same group.
     *
     * Memoized by the messages array reference: local state changes (scroll position, date header,
     * "new messages" badge) trigger re-renders without changing the message list, so we return the
     * cached grouping instead of re-walking the whole list every render.
     *
     * @private
     * @returns {Array<Array<Object>>}
     */
    _getMessagesGroupedBySender() {
        if (this.props.messages === this._groupsCacheKey) {
            return this._groupsCacheValue;
        }

        const groups = this._computeMessagesGroupedBySender();

        this._groupsCacheKey = this.props.messages;
        this._groupsCacheValue = groups;

        return groups;
    }

    /**
     * Performs the actual grouping walk over the message list.
     *
     * @private
     * @returns {Array<Array<Object>>}
     */
    _computeMessagesGroupedBySender() {
        const messagesCount = this.props.messages.length;
        const groups = [];
        let currentGrouping = [];
        let currentGroupSender;
        let currentGroupSource;
        let currentGroupLocal;

        for (let i = 0; i < messagesCount; i++) {
            const message = this.props.messages[i];
            const sender = message.senderIdentityKey || message.id;

            // Visitor chat must stay visually separate from main-room chat, so we never group a
            // visitor message with a regular message (or vice versa) even when the participant id
            // matches. Missing source is treated as main-room.
            const messageSource = message.source === 'visitor' ? 'visitor' : 'main';

            // The same person on another device shares the identity key, but
            // own and remote messages render on different sides.
            const isLocal = message.messageType === MESSAGE_TYPE_LOCAL;

            if (sender === currentGroupSender && messageSource === currentGroupSource
                    && isLocal === currentGroupLocal) {
                currentGrouping.push(message);
            } else {
                currentGrouping.length && groups.push(currentGrouping);

                currentGrouping = [ message ];
                currentGroupSender = sender;
                currentGroupSource = messageSource;
                currentGroupLocal = isLocal;
            }
        }

        currentGrouping.length && groups.push(currentGrouping);

        return groups;
    }
}
